import React, { useEffect, useMemo, useState } from "react";
import { Card, Form } from "react-bootstrap";
import VerifiedIcon from "@mui/icons-material/Verified";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import useENS from "../../../../hooks/useENS";
import { getAssets } from "../../../../store/reducers/asset";
import { saveUserProfileInfo } from "../../../../store/reducers/profile";
import "./index.css";
import useBasicProfile from "../../../../hooks/useBasicProfile";

interface IProps {
  setOnEditProfile: React.Dispatch<React.SetStateAction<boolean>>;
  handleSetAlert: any;
}

const EditPage: React.FC<IProps> = ({ setOnEditProfile, handleSetAlert }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { currentAddress, authenticateStatus } = useAppSelector(
    (state) => state.auth
  );
  const { basicProfile: userProfile, refetch } =
    useBasicProfile(currentAddress);
  const { assets: assetList } = useAppSelector((state) => state.asset);
  const [usernameValue, setUsernameValue] = useState(userProfile?.name || "");
  const [descValue, setDescValue] = useState(userProfile?.description || "");
  const assets = assetList[currentAddress] ?? {};
  const { result } = useENS({ address: currentAddress });

  useEffect(() => {
    if (assets.ens) return;
    dispatch(getAssets("ens"));
  }, [authenticateStatus, currentAddress]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    dispatch(
      saveUserProfileInfo(
        {
          name: usernameValue,
          description: descValue,
          isENSVerified: usernameValue === result,
        },
        refetch,
        handleSetAlert
      )
    );
    setOnEditProfile(false);
  };

  const ENSList = useMemo(
    () =>
      assets.ens
        ?.filter((a: any) => a.name[0])
        .map((asset) => ({
          firstLetter: asset.name[0]?.toUpperCase(),
          title: asset.name,
        })),
    [assets.ens]
  );

  const getNameField = (params?: any) => {
    return (
      <TextField
        value={usernameValue}
        onChange={(e) => {
          setUsernameValue(e.target.value);
        }}
        variant="standard"
        size="small"
        style={{ width: "97%" }}
        placeholder={t("FlashChat_Profile_Username_Placeholder")}
        {...params}
      />
    );
  };

  return (
    <Card>
      <Card.Body
        style={{
          width: "270px",
        }}
      >
        <form className="profile-edit-form" onSubmit={onSubmit}>
          <Form.Group>
            <Form.Label
              style={{
                display: "block",
                color: "#ddd",
                fontSize: "13px",
                fontWeight: "bold",
                margin: "10px 24px 10px 0",
              }}
            >
              {t("FlashChat_Profile_Username")}
            </Form.Label>
            {ENSList?.length ? (
              <div className="profile-edit-username">
                <Autocomplete
                  options={ENSList.sort(
                    (a, b) => -b.firstLetter.localeCompare(a.firstLetter)
                  )}
                  disableListWrap
                  freeSolo
                  // @ts-ignore
                  value={usernameValue}
                  // @ts-ignore
                  onChange={(e, newValue) =>
                    setUsernameValue(
                      (typeof newValue === "string"
                        ? newValue
                        : newValue?.title) || ""
                    )
                  }
                  getOptionLabel={(option) =>
                    (typeof option === "string" ? option : option?.title) || ""
                  }
                  inputValue={usernameValue}
                  onInputChange={(e, newValue) =>
                    setUsernameValue(newValue ?? "")
                  }
                  renderInput={(params) => getNameField(params)}
                  renderOption={(props, option) => (
                    <li style={{ fontSize: "12px" }} {...props}>
                      {result === option.title ? (
                        <VerifiedIcon color="primary" fontSize="small" />
                      ) : (
                        <CheckCircleOutlineIcon
                          color="disabled"
                          fontSize="small"
                        />
                      )}
                      <span className="ens-name">{option.title}</span>
                      {result === option.title ? (
                        <span
                          className="ens-status"
                          style={{ color: "#90caf9" }}
                        >
                          (verified)
                        </span>
                      ) : (
                        <span className="ens-status">(owned)</span>
                      )}
                    </li>
                  )}
                />
              </div>
            ) : (
              getNameField()
            )}
          </Form.Group>
          <Form.Group
            style={{
              marginTop: "5px",
            }}
          >
            <Form.Label
              style={{
                display: "block",
                color: "#ddd",
                fontSize: "0.6rem",
                fontWeight: "bold",
                margin: "0.5rem 0px",
              }}
            >
              {t("FlashChat_Profile_Description")}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              type="text"
              placeholder={t("FlashChat_Profile_Description_Placeholder")}
              name="description"
              value={descValue}
              style={{
                background: "#111111aa",
                color: "#ddd",
                fontSize: "0.6rem",
                fontFamily: "sans-serif",
                width: "97%",
                minWidth: "97%",
                maxWidth: "97%",
                minHeight: "50px",
                maxHeight: "100px",
                border: "1px solid #444",
                padding: "3px",
              }}
              onChange={(e) => {
                setDescValue(e.target.value);
              }}
            />
          </Form.Group>
          <div className="profile-button-group">
            <button className="profile-save-button" type="submit">
              {t("FlashChat_Profile_Save")}
            </button>
            <button
              className="profile-cancel-group"
              onClick={() => {
                setOnEditProfile(false);
              }}
            >
              {t("FlashChat_Profile_Cancel")}
            </button>
          </div>
        </form>
      </Card.Body>
    </Card>
  );
};

export default EditPage;
